import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { AuthScreen, type AuthResult } from "./components/AuthScreen";
import { AdminAccessDenied, AdminLoginScreen } from "./components/AdminLoginScreen";
import { Dashboard } from "./components/Dashboard";
import { LoaderCircle } from "lucide-react";

export type CurrentUser = {
  user: {
    id: string;
    username: string;
    displayName: string;
    role: "USER" | "OPERATOR";
    personaId: string;
    personaName: string;
    personaStatus: string;
  };
  persona: {
    id: string;
    name: string;
    avatar_url: string | null;
    bio: string;
    status: string;
    onboarding_status: string;
    online_status: string;
    auto_mode_enabled: number;
  };
  governanceAccess?: boolean;
};

export default function App() {
  const adminPortal = /^\/admin_login\/?$/.test(window.location.pathname);
  const [current, setCurrent] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api<CurrentUser>("/auth/me");
      setCurrent(me);
    } catch {
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleAuth = (result: AuthResult) => {
    if (result.recoveryCode) setRecoveryCode(result.recoveryCode);
    void refresh();
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="brand-mark brand-mark--large"><span>PS</span></div>
        <LoaderCircle className="spin" size={24} />
        <p>正在唤醒你的数字人格…</p>
      </div>
    );
  }

  if (!current) {
    return adminPortal
      ? <AdminLoginScreen onAuthenticated={() => void refresh()} />
      : <AuthScreen onAuthenticated={handleAuth} />;
  }

  if (adminPortal && current.user.role !== "OPERATOR") {
    return <AdminAccessDenied onLogout={() => {
      void api("/auth/logout", { method: "POST" }).finally(() => setCurrent(null));
    }} />;
  }

  return (
    <>
      <Dashboard
        current={current}
        onRefresh={refresh}
        onLogout={() => setCurrent(null)}
        initialPage={adminPortal ? "governance" : "overview"}
        adminMode={adminPortal}
      />
      {recoveryCode && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card recovery-card">
            <div className="eyebrow">唯一一次显示</div>
            <h2>保存你的账号恢复码</h2>
            <p>它不会以明文保存在平台。若同时遗失密码和恢复码，V1 无法帮你找回账号。</p>
            <div className="recovery-code">{recoveryCode}</div>
            <button className="button button--ghost" onClick={() => void navigator.clipboard.writeText(recoveryCode)}>复制恢复码</button>
            <button className="button button--primary" onClick={() => setRecoveryCode(null)}>我已安全保存</button>
          </div>
        </div>
      )}
    </>
  );
}
